import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, update } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon,
  ChevronDownIcon, ChevronRightIcon, AdjustmentsHorizontalIcon,
  CubeIcon, ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon,
  PlusIcon, MinusIcon, PencilIcon, TagIcon, ChevronLeftIcon,
  Bars3Icon, ArrowPathIcon, SparklesIcon, FolderIcon,
} from '@heroicons/react/24/outline';

// ─── SWAL z-index ─────────────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'inv-swal-z' } };
if (typeof document !== 'undefined' && !document.getElementById('inv-swal-z-style')) {
  const s = document.createElement('style');
  s.id = 'inv-swal-z-style';
  s.textContent = '.inv-swal-z{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getTotalStock = (variants = [], type) => {
  if (!variants?.length) return 0;
  if (type === 'simple') return variants[0]?.quantity || 0;
  if (type === 'size_only') return variants.reduce((s, v) => s + (v.quantity || 0), 0);
  if (type === 'color_size') return variants.reduce((s, v) =>
    s + (v.sizes || []).reduce((ss, sz) => ss + (sz.quantity || 0), 0), 0);
  return 0;
};

const getStockStatus = (stock, alert = 5) => {
  if (stock === 0) return 'out';
  if (stock <= alert) return 'low';
  return 'ok';
};

const STATUS_COLORS = {
  out: { bg: '#fef2f2', text: '#dc2626', dot: '#dc2626', label: 'Out of Stock' },
  low: { bg: '#fffbeb', text: '#d97706', dot: '#f59e0b', label: 'Low Stock' },
  ok:  { bg: '#f0fdf4', text: '#16a34a', dot: '#22c55e', label: 'In Stock' },
};

const fmtNum = (n) => (n ?? 0).toLocaleString();

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
const exportPDF = (rows, title = 'Inventory Report') => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });

  const tableRows = rows.map(r => `
    <tr>
      <td>${r.name}</td>
      <td style="font-family:monospace">${r.sku || '—'}</td>
      <td>${r.brand || '—'}</td>
      <td>${r.category}</td>
      <td style="font-size:10px">${r.variantDetails}</td>
      <td style="text-align:right">${fmtNum(r.stock)}</td>
      <td style="text-align:right">${r.alert}</td>
      <td style="color:${STATUS_COLORS[r.status].text};font-weight:600">${STATUS_COLORS[r.status].label}</td>
      <td style="text-align:right">KES ${fmtNum(r.price)}</td>
    </tr>
  `).join('');

  const totalStock = rows.reduce((s, r) => s + r.stock, 0);
  const outCount = rows.filter(r => r.status === 'out').length;
  const lowCount = rows.filter(r => r.status === 'low').length;

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #1a1a2e; padding: 24px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
    .title { font-size: 20px; font-weight: 800; color: #800020; }
    .subtitle { font-size: 11px; color: #6b7280; margin-top: 2px; }
    .summary { display: flex; gap: 16px; margin-bottom: 16px; }
    .stat { background: #f8f9fa; border-radius: 8px; padding: 8px 14px; }
    .stat-val { font-size: 18px; font-weight: 700; color: #1a1a2e; }
    .stat-lbl { font-size: 10px; color: #6b7280; margin-top: 1px; }
    .stat.danger .stat-val { color: #dc2626; }
    .stat.warn .stat-val { color: #d97706; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    thead tr { background: #1a1a2e; color: #fff; }
    thead th { padding: 8px 6px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
    tbody tr:nth-child(even) { background: #f8f9fa; }
    tbody tr:hover { background: #fef2f2; }
    td { padding: 6px 6px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
    .footer { margin-top: 16px; font-size: 10px; color: #9ca3af; text-align: right; }
    @media print { body { padding: 10mm; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">📦 ${title}</div>
      <div class="subtitle">Generated on ${dateStr} · ${rows.length} products</div>
    </div>
  </div>
  <div class="summary">
    <div class="stat"><div class="stat-val">${rows.length}</div><div class="stat-lbl">Total Products</div></div>
    <div class="stat"><div class="stat-val">${fmtNum(totalStock)}</div><div class="stat-lbl">Total Units</div></div>
    <div class="stat danger"><div class="stat-val">${outCount}</div><div class="stat-lbl">Out of Stock</div></div>
    <div class="stat warn"><div class="stat-val">${lowCount}</div><div class="stat-lbl">Low Stock</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Product</th><th>SKU</th><th>Brand</th><th>Category</th>
        <th>Variant Details</th><th style="text-align:right">Total Stock</th>
        <th style="text-align:right">Alert Level</th><th>Status</th>
        <th style="text-align:right">Price</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div class="footer">Inventory Management System · ${dateStr}</div>
  <script>window.onload=()=>{ window.print(); }</script>
</body>
</html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const InventoryManagement = () => {
  const { userData } = useAuth();
  const userRole = (userData?.personalInfo?.role || '').toLowerCase();
  const isAuthorized = userRole === 'admin' || userRole === 'superadmin';

  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);

  // filters
  const [search,         setSearch]        = useState('');
  const [filterCat,      setFilterCat]     = useState('');
  const [filterSub,      setFilterSub]     = useState('');
  const [filterSubSub,   setFilterSubSub]  = useState('');
  const [filterStatus,   setFilterStatus]  = useState('all');
  const [filterType,     setFilterType]    = useState('all');

  // sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // modals
  const [adjustModal, setAdjustModal]   = useState(null);
  const [alertModal,  setAlertModal]    = useState(null);

  // selection for export
  const [selected, setSelected] = useState(new Set());

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pSnap, cSnap] = await Promise.all([
        get(ref(database, 'products')),
        get(ref(database, 'categories')),
      ]);
      setProducts(pSnap.exists() ? Object.entries(pSnap.val()).map(([k, v]) => ({ id: k, ...v })) : []);
      setCategories(cSnap.exists() ? Object.entries(cSnap.val()).map(([k, v]) => ({ id: k, ...v })) : []);
    } catch (e) { console.error(e); toast('error', 'Failed to load data'); }
    finally { setLoading(false); }
  };

  // ─── derived ────────────────────────────────────────────────────────────────
  const mainCats  = categories.filter(c => c.level === 0 && !c.parentId);
  const subCats   = categories.filter(c => c.parentId === filterCat  && c.level === 1);
  const subSubCats = categories.filter(c => c.parentId === filterSub && c.level === 2);

  const catName = (id) => categories.find(c => c.id === id)?.name || '—';

  const getVariantDetails = (product) => {
    if (!product.variants?.length) return '—';
    
    if (product.variantType === 'simple') {
      const v = product.variants[0];
      return `${v.quantity} ${v.unit || 'pcs'}`;
    }
    
    if (product.variantType === 'size_only') {
      return product.variants.map(v => `${v.size}: ${v.quantity} ${v.unit || 'pcs'}`).join(', ');
    }
    
    if (product.variantType === 'color_size') {
      return product.variants.map(v => {
        const sizes = v.sizes.map(sz => `${sz.size}:${sz.quantity}`).join(', ');
        return `${v.color} (${sizes})`;
      }).join('; ');
    }
    
    return '—';
  };

  const filteredProducts = products.filter(p => {
    if (filterSubSub && p.subSubCategoryId !== filterSubSub) return false;
    if (!filterSubSub && filterSub && p.subCategoryId !== filterSub) return false;
    if (!filterSubSub && !filterSub && filterCat && p.categoryId !== filterCat) return false;
    if (filterType !== 'all' && p.variantType !== filterType) return false;
    const stock  = getTotalStock(p.variants, p.variantType);
    const status = getStockStatus(stock, p.lowStockAlert ?? 5);
    if (filterStatus !== 'all' && status !== filterStatus) return false;
    const q = search.toLowerCase();
    if (q && !p.name?.toLowerCase().includes(q) &&
             !p.sku?.toLowerCase().includes(q) &&
             !p.brand?.toLowerCase().includes(q)) return false;
    return true;
  });

  // rows for PDF
  const buildRows = (pList) => pList.map(p => {
    const stock  = getTotalStock(p.variants, p.variantType);
    const status = getStockStatus(stock, p.lowStockAlert ?? 5);
    return {
      id: p.id, name: p.name, sku: p.sku, brand: p.brand,
      category: catName(p.categoryId),
      variantDetails: getVariantDetails(p),
      stock, alert: p.lowStockAlert ?? 5, status, price: p.price,
    };
  });

  const handleExportAll      = () => exportPDF(buildRows(filteredProducts), 'Full Inventory Report');
  const handleExportSelected = () => {
    const sel = filteredProducts.filter(p => selected.has(p.id));
    if (!sel.length) return toast('warning', 'No products selected');
    exportPDF(buildRows(sel), 'Selected Inventory Report');
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === filteredProducts.length) setSelected(new Set());
    else setSelected(new Set(filteredProducts.map(p => p.id)));
  };

  const clearFilters = () => {
    setSearch(''); setFilterCat(''); setFilterSub('');
    setFilterSubSub(''); setFilterStatus('all'); setFilterType('all');
  };

  const stats = {
    total: products.length,
    out:   products.filter(p => getTotalStock(p.variants, p.variantType) === 0).length,
    low:   products.filter(p => { const s = getTotalStock(p.variants, p.variantType); return s > 0 && s <= (p.lowStockAlert ?? 5); }).length,
    units: products.reduce((t, p) => t + getTotalStock(p.variants, p.variantType), 0),
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
              {userRole && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                  style={{
                    background: userRole === 'superadmin' ? '#1a1a2e' : isAuthorized ? '#fef2f2' : '#f3f4f6',
                    color: userRole === 'superadmin' ? '#fff' : isAuthorized ? '#800020' : '#6b7280',
                  }}>
                  {userRole === 'superadmin' ? '⚡ Super Admin' : isAuthorized ? '🛡 Admin' : '👁 View Only'}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1 text-sm">Track stock levels, adjust quantities and export reports</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleExportSelected}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all"
              style={{ borderColor: '#800020', color: '#800020' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export Selected
            </button>
            <button onClick={handleExportAll}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export All
            </button>
          </div>
        </div>

        {/* STAT PILLS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Products', val: stats.total, color: '#1a1a2e' },
            { label: 'Total Units',    val: fmtNum(stats.units), color: '#2563eb' },
            { label: 'Out of Stock',   val: stats.out, color: '#dc2626' },
            { label: 'Low Stock',      val: stats.low, color: '#d97706' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-3">
              <div>
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.val}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* LAYOUT */}
        <div className="flex gap-5 items-start">

          {/* SIDEBAR */}
          <AnimatePresence initial={false}>
            {sidebarOpen && (
              <motion.div
                key="inv-sidebar"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 260, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: 'spring', damping: 26, stiffness: 280 }}
                style={{ flexShrink: 0, overflow: 'hidden' }}>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" style={{ width: 260 }}>
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Filter by Category</span>
                    <button onClick={() => setSidebarOpen(false)}
                      className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                      <ChevronLeftIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="p-2 overflow-y-auto" style={{ maxHeight: '65vh' }}>
                    <SidebarItem
                      active={!filterCat}
                      onClick={() => { setFilterCat(''); setFilterSub(''); setFilterSubSub(''); }}
                      label="All Products"
                      icon={<SparklesIcon className="h-3.5 w-3.5" />}
                      count={products.length}
                    />
                    {mainCats.map(cat => (
                      <CatFilterNode key={cat.id}
                        cat={cat} categories={categories} products={products}
                        filterCat={filterCat} filterSub={filterSub} filterSubSub={filterSubSub}
                        setFilterCat={v => { setFilterCat(v); setFilterSub(''); setFilterSubSub(''); }}
                        setFilterSub={v => { setFilterSub(v); setFilterSubSub(''); }}
                        setFilterSubSub={setFilterSubSub}
                      />
                    ))}
                  </div>

                  {/* Status quick-filter */}
                  <div className="border-t border-gray-100 p-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide px-1 mb-2">Stock Status</p>
                    {[
                      { val: 'all', label: 'All',          color: '#6b7280' },
                      { val: 'ok',  label: 'In Stock',     color: '#16a34a' },
                      { val: 'low', label: 'Low Stock',    color: '#d97706' },
                      { val: 'out', label: 'Out of Stock', color: '#dc2626' },
                    ].map(s => (
                      <button key={s.val} onClick={() => setFilterStatus(s.val)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all mb-1"
                        style={{
                          background: filterStatus === s.val ? s.color + '18' : 'transparent',
                          color: filterStatus === s.val ? s.color : '#6b7280',
                          fontWeight: filterStatus === s.val ? 700 : 500,
                        }}>
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* MAIN TABLE AREA */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {!sidebarOpen && (
                <button onClick={() => setSidebarOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all flex-shrink-0">
                  <Bars3Icon className="h-4 w-4" />
                </button>
              )}
              <div className="flex-1 relative min-w-0" style={{ minWidth: 160 }}>
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search name, SKU, brand…"
                  className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 shadow-sm transition-all"
                  style={{ '--tw-ring-color': '#800020' }} />
              </div>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none shadow-sm">
                <option value="all">All Types</option>
                <option value="simple">Simple</option>
                <option value="size_only">Size/Variant</option>
                <option value="color_size">Colour+Size</option>
              </select>
              {(search || filterCat || filterStatus !== 'all' || filterType !== 'all') && (
                <button onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 shadow-sm transition-all">
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Clear
                </button>
              )}
              <button onClick={toggleAll}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:border-gray-300 shadow-sm transition-all flex-shrink-0">
                {selected.size === filteredProducts.length && filteredProducts.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Count */}
            <p className="text-xs text-gray-400 mb-3 font-medium">
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </p>

            {/* TABLE */}
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block w-8 h-8 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
                <p className="text-gray-500 mt-3 text-sm">Loading inventory…</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <CubeIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-medium text-sm">No products match your filters</p>
                <button onClick={clearFilters} className="mt-3 text-sm font-semibold" style={{ color: '#800020' }}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}>
                        <th className="w-8 px-3 py-3">
                          <input type="checkbox"
                            checked={selected.size === filteredProducts.length && filteredProducts.length > 0}
                            onChange={toggleAll}
                            className="rounded" />
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Product</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide hidden sm:table-cell">SKU</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide hidden md:table-cell">Category</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Variant Details</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-white/80 uppercase tracking-wide">Total Stock</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-white/80 uppercase tracking-wide hidden lg:table-cell">Alert Level</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wide">Status</th>
                        {isAuthorized && (
                          <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wide">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product, idx) => (
                        <InventoryRow
                          key={product.id}
                          product={product}
                          idx={idx}
                          categories={categories}
                          isAuthorized={isAuthorized}
                          selected={selected.has(product.id)}
                          onToggleSelect={() => toggleSelect(product.id)}
                          onAdjust={() => setAdjustModal(product)}
                          onSetAlert={() => setAlertModal(product)}
                          catName={catName}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ADJUST MODAL - FULL SCREEN */}
      <AnimatePresence>
        {adjustModal && (
          <AdjustModal
            product={adjustModal}
            categories={categories}
            isAuthorized={isAuthorized}
            onClose={() => setAdjustModal(null)}
            onSaved={() => { setAdjustModal(null); fetchAll(); }}
          />
        )}
      </AnimatePresence>

      {/* ALERT LEVEL MODAL - FULL SCREEN */}
      <AnimatePresence>
        {alertModal && (
          <AlertModal
            product={alertModal}
            isAuthorized={isAuthorized}
            onClose={() => setAlertModal(null)}
            onSaved={() => { setAlertModal(null); fetchAll(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SIDEBAR HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
const SidebarItem = ({ active, onClick, label, icon, count }) => (
  <button onClick={onClick}
    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all mb-0.5"
    style={{ background: active ? '#800020' : 'transparent', color: active ? '#fff' : '#374151' }}>
    {icon}
    <span className="flex-1 text-left truncate">{label}</span>
    {count != null && <span className="text-xs opacity-60 font-bold">{count}</span>}
  </button>
);

const CatFilterNode = ({ cat, categories, products, filterCat, filterSub, filterSubSub, setFilterCat, setFilterSub, setFilterSubSub }) => {
  const [open, setOpen] = useState(false);
  const subs = categories.filter(c => c.parentId === cat.id && c.level === 1);
  const isActive = filterCat === cat.id && !filterSub;
  const count = products.filter(p => p.categoryId === cat.id).length;

  return (
    <div>
      <div className="flex items-center rounded-lg mb-0.5" style={{ background: isActive ? '#800020' : 'transparent' }}>
        <button onClick={() => setOpen(o => !o)}
          className="w-6 h-7 flex items-center justify-center flex-shrink-0"
          style={{ color: isActive ? 'rgba(255,255,255,0.7)' : '#9ca3af' }}>
          {subs.length > 0 ? (open ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />) : <span className="w-3" />}
        </button>
        <button onClick={() => setFilterCat(cat.id)}
          className="flex-1 flex items-center gap-1.5 py-1.5 text-xs font-medium text-left min-w-0"
          style={{ color: isActive ? '#fff' : '#374151' }}>
          {cat.imageUrl
            ? <img src={cat.imageUrl} alt="" className="w-4 h-4 rounded object-cover flex-shrink-0" />
            : <FolderIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
          }
          <span className="truncate">{cat.name}</span>
        </button>
        {count > 0 && <span className="mr-1.5 text-xs font-bold opacity-50 flex-shrink-0" style={{ color: isActive ? '#fff' : '#9ca3af' }}>{count}</span>}
      </div>

      <AnimatePresence>
        {open && subs.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden ml-4">
            {subs.map(sub => {
              const subSubs = categories.filter(c => c.parentId === sub.id && c.level === 2);
              const subActive = filterSub === sub.id && !filterSubSub;
              return (
                <div key={sub.id}>
                  <button onClick={() => setFilterSub(sub.id)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-left mb-0.5 transition-all"
                    style={{ background: subActive ? '#9a1a3a' : 'transparent', color: subActive ? '#fff' : '#6b7280' }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: subActive ? '#fff' : '#d1d5db' }} />
                    <span className="truncate">{sub.name}</span>
                  </button>
                  {filterSub === sub.id && subSubs.length > 0 && (
                    <div className="ml-3">
                      {subSubs.map(ss => (
                        <button key={ss.id} onClick={() => setFilterSubSub(ss.id)}
                          className="w-full flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-left mb-0.5 transition-all"
                          style={{ background: filterSubSub === ss.id ? '#b91c1c' : 'transparent', color: filterSubSub === ss.id ? '#fff' : '#9ca3af' }}>
                          <span className="w-1 h-1 rounded-full flex-shrink-0 bg-current opacity-50" />
                          <span className="truncate">{ss.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// INVENTORY ROW - IMPROVED WITH FULL VARIANT DETAILS
// ═══════════════════════════════════════════════════════════════════════════════
const InventoryRow = ({ product, idx, categories, isAuthorized, selected, onToggleSelect, onAdjust, onSetAlert, catName }) => {
  const stock  = getTotalStock(product.variants, product.variantType);
  const status = getStockStatus(stock, product.lowStockAlert ?? 5);
  const sc     = STATUS_COLORS[status];
  const img    = product.images?.[0];

  const renderVariantDetails = () => {
    if (!product.variants?.length) return <span className="text-gray-400 text-xs">No variants</span>;
    
    if (product.variantType === 'simple') {
      const v = product.variants[0];
      return (
        <div className="flex items-center gap-2">
          <CubeIcon className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">{v.quantity}</span>
          <span className="text-xs text-gray-500">({v.unit || 'pcs'})</span>
        </div>
      );
    }
    
    if (product.variantType === 'size_only') {
      return (
        <div className="space-y-1.5">
          {product.variants.map((v, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <TagIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="font-semibold text-gray-700 min-w-[50px]">{v.size}:</span>
              <span className="text-gray-600">{v.quantity}</span>
              <span className="text-gray-400 text-[10px]">({v.unit || 'pcs'})</span>
            </div>
          ))}
        </div>
      );
    }
    
    if (product.variantType === 'color_size') {
      return (
        <div className="space-y-2">
          {product.variants.map((v, vi) => {
            const colorTotal = (v.sizes || []).reduce((s, sz) => s + (sz.quantity || 0), 0);
            return (
              <div key={vi} className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#800020' }} />
                  <span className="text-xs font-bold text-gray-800">{v.color}</span>
                  <span className="text-[10px] text-gray-500">({colorTotal} total)</span>
                </div>
                <div className="ml-4 space-y-0.5">
                  {(v.sizes || []).map((sz, si) => (
                    <div key={si} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 w-8">{sz.size}:</span>
                      <span className="text-gray-700 font-medium">{sz.quantity}</span>
                      <span className="text-gray-400 text-[10px]">pcs</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    
    return <span className="text-gray-400 text-xs">—</span>;
  };

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: idx * 0.02 }}
      className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors"
      style={{ background: selected ? '#fef2f2' : undefined }}>

      <td className="px-3 py-2.5 text-center">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} className="rounded" />
      </td>

      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
            {img
              ? <img src={img} alt="" className="w-full h-full" style={{ objectFit: 'contain', padding: 2 }} />
              : <div className="w-full h-full flex items-center justify-center"><CubeIcon className="h-4 w-4 text-gray-300" /></div>
            }
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate max-w-[160px]">{product.name}</p>
            {product.brand && <p className="text-xs text-gray-400 truncate">{product.brand}</p>}
          </div>
        </div>
      </td>

      <td className="px-3 py-2.5 hidden sm:table-cell">
        <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{product.sku || '—'}</span>
      </td>

      <td className="px-3 py-2.5 hidden md:table-cell">
        <p className="text-xs text-gray-600 truncate max-w-[120px]">{catName(product.categoryId)}</p>
        {product.subCategoryId && (
          <p className="text-xs text-gray-400 truncate max-w-[120px]">{catName(product.subCategoryId)}</p>
        )}
      </td>

      <td className="px-3 py-2.5 max-w-[280px]">
        {renderVariantDetails()}
      </td>

      <td className="px-3 py-2.5 text-right">
        <span className="text-sm font-bold" style={{ color: sc.text }}>{fmtNum(stock)}</span>
      </td>

      <td className="px-3 py-2.5 text-right hidden lg:table-cell">
        <span className="text-xs text-gray-500">{product.lowStockAlert ?? 5}</span>
      </td>

      <td className="px-3 py-2.5 text-center">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: sc.bg, color: sc.text }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: sc.dot }} />
          <span className="hidden sm:inline">{sc.label}</span>
        </span>
      </td>

      {isAuthorized && (
        <td className="px-3 py-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <button onClick={onAdjust} title="Adjust stock"
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
              style={{ background: '#eff6ff', color: '#2563eb' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
              onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}>
              <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
              Adjust
            </button>
            <button onClick={onSetAlert} title="Set low stock alert level"
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
              style={{ background: '#fffbeb', color: '#d97706' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
              onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}>
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              Alert
            </button>
          </div>
        </td>
      )}
    </motion.tr>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADJUST STOCK MODAL - FULL SCREEN WITH IMPROVED DELTA INPUT
// ═══════════════════════════════════════════════════════════════════════════════
const AdjustModal = ({ product, isAuthorized, onClose, onSaved }) => {
  const [variants, setVariants]   = useState(() => JSON.parse(JSON.stringify(product.variants || [])));
  const [saving,   setSaving]     = useState(false);
  const [expandedColor, setExpandedColor] = useState(null);
  const [deltas, setDeltas] = useState({});

  const getKey = (vi, si) => si != null ? `${vi}_${si}` : `${vi}`;

  const applyDelta = (vi, si, sign) => {
    const key = getKey(vi, si);
    const delta = parseInt(deltas[key] || '0') || 0;
    if (delta === 0) {
      toast('info', 'No change', 'Enter an amount to adjust first');
      return;
    }
    setVariants(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (si != null) {
        next[vi].sizes[si].quantity = Math.max(0, (next[vi].sizes[si].quantity || 0) + sign * delta);
      } else {
        next[vi].quantity = Math.max(0, (next[vi].quantity || 0) + sign * delta);
      }
      return next;
    });
    setDeltas(prev => ({ ...prev, [key]: '' }));
  };

  const setExact = (vi, si, val) => {
    const v = Math.max(0, parseInt(val) || 0);
    setVariants(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (si != null) next[vi].sizes[si].quantity = v;
      else next[vi].quantity = v;
      return next;
    });
  };

  const save = async () => {
    if (!isAuthorized) return toast('error', 'Access Denied');
    setSaving(true);
    try {
      await update(ref(database, `products/${product.id}`), {
        variants,
        updatedAt: Date.now(),
      });
      toast('success', 'Stock updated successfully');
      onSaved();
    } catch (e) { 
      console.error(e); 
      toast('error', 'Failed to update', e?.message);
    } finally { 
      setSaving(false); 
    }
  };

  const totalStock = getTotalStock(variants, product.variantType);

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div className="flex items-center gap-3">
            {product.images?.[0] && (
              <img src={product.images[0]} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white/20" />
            )}
            <div>
              <h2 className="text-xl font-bold text-white">Adjust Stock — {product.name}</h2>
              <p className="text-white/60 text-sm mt-0.5">
                {product.variantType === 'simple' ? 'Single quantity product' :
                 product.variantType === 'size_only' ? 'Size variants' : 'Color & size combinations'}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6" style={{ minHeight: 0 }}>
          <div className="max-w-4xl mx-auto space-y-6">

            {/* Stock Summary Card */}
            <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Current Total Stock</p>
                  <p className="text-3xl font-bold" style={{ color: '#800020' }}>{fmtNum(totalStock)} units</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-1">Low Stock Alert</p>
                  <p className="text-lg font-semibold text-gray-700">{product.lowStockAlert ?? 5} units</p>
                </div>
              </div>
            </div>

            {/* ── SIMPLE ── */}
            {product.variantType === 'simple' && variants[0] && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100" style={{ background: '#faf5ff' }}>
                  <h3 className="text-base font-bold text-gray-800">Quantity Adjustment</h3>
                  <p className="text-xs text-gray-500 mt-1">Unit: {variants[0].unit || 'pcs'}</p>
                </div>
                <div className="p-6 space-y-5">
                  {/* Delta Adjustment Section */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                      Adjustment Amount (Δ)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Enter a number, then click + to add or - to subtract from current stock</p>
                    <div className="flex items-center gap-3">
                      <button onClick={() => applyDelta(0, null, -1)}
                        className="w-12 h-12 flex items-center justify-center rounded-xl border-2 transition-all"
                        style={{ borderColor: '#dc2626', color: '#dc2626' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        title="Subtract adjustment amount">
                        <MinusIcon className="h-6 w-6" />
                      </button>
                      <div className="flex-1">
                        <input
                          type="number"
                          value={deltas[getKey(0)] ?? ''}
                          onChange={e => setDeltas(p => ({ ...p, [getKey(0)]: e.target.value }))}
                          placeholder="Enter amount to adjust (e.g., 25)"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base text-center focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                          style={{ '--tw-ring-color': '#800020' }}
                        />
                      </div>
                      <button onClick={() => applyDelta(0, null, 1)}
                        className="w-12 h-12 flex items-center justify-center rounded-xl border-2 transition-all"
                        style={{ borderColor: '#16a34a', color: '#16a34a' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        title="Add adjustment amount">
                        <PlusIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Exact Quantity Section */}
                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                      Set Exact Quantity
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number" min="0"
                        value={variants[0].quantity ?? 0}
                        onChange={e => setExact(0, null, e.target.value)}
                        className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-base text-center focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{ '--tw-ring-color': '#800020' }}
                      />
                      <span className="text-sm text-gray-500 whitespace-nowrap">exact units</span>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl mt-2"
                    style={{ background: STATUS_COLORS[getStockStatus(variants[0].quantity || 0, product.lowStockAlert ?? 5)].bg }}>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ background: STATUS_COLORS[getStockStatus(variants[0].quantity || 0, product.lowStockAlert ?? 5)].dot }} />
                      <span className="text-sm font-semibold"
                        style={{ color: STATUS_COLORS[getStockStatus(variants[0].quantity || 0, product.lowStockAlert ?? 5)].text }}>
                        Current: {variants[0].quantity || 0} {variants[0].unit || 'pcs'} in stock
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── SIZE ONLY ── */}
            {product.variantType === 'size_only' && (
              <div className="space-y-4">
                {variants.map((v, vi) => {
                  const status = getStockStatus(v.quantity || 0, product.lowStockAlert ?? 5);
                  return (
                    <div key={vi} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-100" style={{ background: '#faf5ff' }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-base font-bold text-gray-800">{v.size}</h3>
                            <p className="text-xs text-gray-500">Unit: {v.unit || 'pcs'}</p>
                          </div>
                          <span className="text-sm font-bold px-3 py-1 rounded-full" style={{ background: STATUS_COLORS[status].bg, color: STATUS_COLORS[status].text }}>
                            {fmtNum(v.quantity || 0)} in stock
                          </span>
                        </div>
                      </div>
                      <div className="p-6 space-y-5">
                        {/* Delta Adjustment */}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700">Adjustment Amount (Δ)</label>
                          <div className="flex items-center gap-3">
                            <button onClick={() => applyDelta(vi, null, -1)}
                              className="w-12 h-12 flex items-center justify-center rounded-xl border-2 transition-all"
                              style={{ borderColor: '#dc2626', color: '#dc2626' }}>
                              <MinusIcon className="h-6 w-6" />
                            </button>
                            <div className="flex-1">
                              <input type="number" min="0"
                                value={deltas[getKey(vi)] ?? ''}
                                onChange={e => setDeltas(p => ({ ...p, [getKey(vi)]: e.target.value }))}
                                placeholder="Enter amount to adjust"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base text-center focus:outline-none focus:ring-2"
                                style={{ '--tw-ring-color': '#800020' }}
                              />
                            </div>
                            <button onClick={() => applyDelta(vi, null, 1)}
                              className="w-12 h-12 flex items-center justify-center rounded-xl border-2 transition-all"
                              style={{ borderColor: '#16a34a', color: '#16a34a' }}>
                              <PlusIcon className="h-6 w-6" />
                            </button>
                          </div>
                        </div>

                        {/* Exact Quantity */}
                        <div className="space-y-2 pt-2 border-t border-gray-100">
                          <label className="text-sm font-semibold text-gray-700">Set Exact Quantity</label>
                          <div className="flex items-center gap-3">
                            <input type="number" min="0"
                              value={v.quantity ?? 0}
                              onChange={e => setExact(vi, null, e.target.value)}
                              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-base text-center focus:outline-none focus:ring-2"
                              style={{ '--tw-ring-color': '#800020' }}
                            />
                            <span className="text-sm text-gray-500">units</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── COLOR + SIZE ── */}
            {product.variantType === 'color_size' && (
              <div className="space-y-4">
                {variants.map((v, vi) => {
                  const colorTotal = (v.sizes || []).reduce((s, sz) => s + (sz.quantity || 0), 0);
                  const colorStatus = getStockStatus(colorTotal, product.lowStockAlert ?? 5);
                  const isOpen = expandedColor === vi;

                  return (
                    <div key={vi} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <button 
                        onClick={() => setExpandedColor(isOpen ? null : vi)}
                        className="w-full flex items-center justify-between px-6 py-4 text-left transition-all hover:bg-gray-50"
                        style={{ background: isOpen ? '#fef2f2' : '#fff' }}>
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full" style={{ background: '#800020' }} />
                          <div>
                            <h3 className="text-base font-bold text-gray-800">{v.color}</h3>
                            <p className="text-xs text-gray-500">{v.sizes?.length || 0} sizes</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold px-3 py-1 rounded-full"
                            style={{ background: STATUS_COLORS[colorStatus].bg, color: STATUS_COLORS[colorStatus].text }}>
                            {fmtNum(colorTotal)} total
                          </span>
                          <motion.span animate={{ rotate: isOpen ? 90 : 0 }}>
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          </motion.span>
                        </div>
                      </button>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div 
                            initial={{ height: 0 }} 
                            animate={{ height: 'auto' }} 
                            exit={{ height: 0 }}
                            className="overflow-hidden border-t border-gray-100">
                            <div className="p-6 space-y-4" style={{ background: '#fefafc' }}>
                              {(v.sizes || []).map((sz, si) => {
                                const szStatus = getStockStatus(sz.quantity || 0, product.lowStockAlert ?? 5);
                                return (
                                  <div key={si} className="bg-white rounded-xl p-4 border border-gray-100">
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="font-semibold text-gray-800">{sz.size}</span>
                                      <span className="text-sm font-bold" style={{ color: STATUS_COLORS[szStatus].text }}>
                                        {fmtNum(sz.quantity || 0)} in stock
                                      </span>
                                    </div>
                                    
                                    {/* Delta Adjustment */}
                                    <div className="space-y-2 mb-4">
                                      <label className="text-xs font-semibold text-gray-600">Adjustment Amount (Δ)</label>
                                      <div className="flex items-center gap-2">
                                        <button onClick={() => applyDelta(vi, si, -1)}
                                          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 transition-all"
                                          style={{ borderColor: '#dc2626', color: '#dc2626' }}>
                                          <MinusIcon className="h-5 w-5" />
                                        </button>
                                        <div className="flex-1">
                                          <input type="number" min="0"
                                            value={deltas[getKey(vi, si)] ?? ''}
                                            onChange={e => setDeltas(p => ({ ...p, [getKey(vi, si)]: e.target.value }))}
                                            placeholder="Amount to adjust"
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2"
                                            style={{ '--tw-ring-color': '#800020' }}
                                          />
                                        </div>
                                        <button onClick={() => applyDelta(vi, si, 1)}
                                          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 transition-all"
                                          style={{ borderColor: '#16a34a', color: '#16a34a' }}>
                                          <PlusIcon className="h-5 w-5" />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Exact Quantity */}
                                    <div className="space-y-2">
                                      <label className="text-xs font-semibold text-gray-600">Set Exact Quantity</label>
                                      <div className="flex items-center gap-2">
                                        <input type="number" min="0"
                                          value={sz.quantity ?? 0}
                                          onChange={e => setExact(vi, si, e.target.value)}
                                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2"
                                          style={{ '--tw-ring-color': '#800020' }}
                                        />
                                        <span className="text-xs text-gray-500 whitespace-nowrap">units</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">New total after changes:</span>
            <span className="text-lg font-bold" style={{ color: '#800020' }}>{fmtNum(totalStock)} units</span>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button onClick={save} disabled={saving || !isAuthorized}
              className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ALERT LEVEL MODAL - FULL SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
const AlertModal = ({ product, isAuthorized, onClose, onSaved }) => {
  const [globalAlert, setGlobalAlert]   = useState(product.lowStockAlert ?? 5);
  const [perVariant,  setPerVariant]    = useState(() => JSON.parse(JSON.stringify(product.lowStockPerVariant || {})));
  const [usePerVar,   setUsePerVar]     = useState(false);
  const [saving,      setSaving]        = useState(false);

  const save = async () => {
    if (!isAuthorized) return toast('error', 'Access Denied');
    setSaving(true);
    try {
      await update(ref(database, `products/${product.id}`), {
        lowStockAlert: globalAlert,
        lowStockPerVariant: usePerVar ? perVariant : {},
        updatedAt: Date.now(),
      });
      toast('success', 'Alert levels updated successfully');
      onSaved();
    } catch (e) { 
      console.error(e); 
      toast('error', 'Failed to update', e?.message);
    } finally { 
      setSaving(false); 
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#d97706 100%)' }}>
          <div>
            <h2 className="text-xl font-bold text-white">Low Stock Alert Settings</h2>
            <p className="text-white/60 text-sm mt-0.5">{product.name}</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6" style={{ minHeight: 0 }}>
          <div className="max-w-3xl mx-auto space-y-6">

            {/* Global Alert Card */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-100 rounded-xl">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Global Alert Level</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Products will be flagged as "Low Stock" when total quantity drops to or below this number.
                  </p>
                  <div className="flex items-center gap-4">
                    <button onClick={() => setGlobalAlert(a => Math.max(1, a - 1))}
                      className="w-10 h-10 flex items-center justify-center rounded-xl border-2 transition-all"
                      style={{ borderColor: '#d97706', color: '#d97706' }}>
                      <MinusIcon className="h-5 w-5" />
                    </button>
                    <div className="text-center">
                      <input type="number" min="1"
                        value={globalAlert}
                        onChange={e => setGlobalAlert(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-32 px-4 py-3 border-2 border-amber-300 rounded-xl text-xl font-bold text-center focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': '#d97706' }}
                      />
                      <p className="text-xs text-gray-500 mt-1">units</p>
                    </div>
                    <button onClick={() => setGlobalAlert(a => a + 1)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl border-2 transition-all"
                      style={{ borderColor: '#d97706', color: '#d97706' }}>
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-Variant Alerts */}
            {product.variantType !== 'simple' && product.variants?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div
                      onClick={() => setUsePerVar(v => !v)}
                      className="w-12 h-6 rounded-full transition-all flex-shrink-0 relative cursor-pointer"
                      style={{ background: usePerVar ? '#800020' : '#d1d5db' }}>
                      <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-all"
                        style={{ transform: usePerVar ? 'translateX(24px)' : 'translateX(0)' }} />
                    </div>
                    <div>
                      <span className="text-base font-semibold text-gray-800">Per-variant alert levels</span>
                      <p className="text-xs text-gray-500 mt-0.5">Set different thresholds for each variant</p>
                    </div>
                  </label>
                </div>

                {usePerVar && (
                  <div className="p-6 space-y-3">
                    {product.variantType === 'size_only' && product.variants?.map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50">
                        <div>
                          <span className="font-semibold text-gray-800">{v.size}</span>
                          <span className="text-xs text-gray-500 ml-2">{v.unit || 'pcs'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Alert at:</span>
                          <input type="number" min="1"
                            value={perVariant[i] ?? globalAlert}
                            onChange={e => setPerVariant(p => ({ ...p, [i]: Math.max(1, parseInt(e.target.value) || 1) }))}
                            className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': '#d97706' }}
                          />
                          <span className="text-xs text-gray-500">units</span>
                        </div>
                      </div>
                    ))}

                    {product.variantType === 'color_size' && product.variants?.map((v, vi) => (
                      <div key={vi} className="rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                          <span className="font-semibold text-gray-800">{v.color}</span>
                        </div>
                        <div className="p-3 space-y-2">
                          {(v.sizes || []).map((sz, si) => (
                            <div key={si} className="flex items-center justify-between px-3 py-2">
                              <span className="text-sm text-gray-600">{sz.size}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Alert at:</span>
                                <input type="number" min="1"
                                  value={perVariant[`${vi}_${si}`] ?? globalAlert}
                                  onChange={e => setPerVariant(p => ({ ...p, [`${vi}_${si}`]: Math.max(1, parseInt(e.target.value) || 1) }))}
                                  className="w-20 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2"
                                  style={{ '--tw-ring-color': '#d97706' }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">How it works</p>
                  <p className="text-xs text-blue-600 mt-1">
                    Low stock alerts help you know when to reorder. Set thresholds that make sense for your business.
                    When stock drops to or below the alert level, the product will be highlighted as "Low Stock".
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 px-8 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !isAuthorized}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#d97706,#b45309)' }}>
            {saving ? 'Saving…' : 'Save Alert Levels'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InventoryManagement;